/* =============================================================================
   agb-birthday-trigger.js — arms the birthday band on the shopper's birthday

   WHAT: compares today's date to the birthday we hold for this visitor and
   writes `localStorage.birthday_overlay`, which is the only thing
   agb-birthday-overlay.js looks at. This file never touches that one.

   WHERE THE BIRTHDAY COMES FROM, best source first:
     1. window.AGB_Customer.birthday — the Shopify customer metafield, rendered
        by Liquid. Works on ANY device the shopper is logged in on. Absent until
        the metafield is added to the AGB_Customer line in theme.liquid.
     2. localStorage.AGB_Birthday.date — written by /pages/birthday. This
        browser only.
   Both are "MM-DD". Neither is trusted without re-validation.

   29 FEBRUARY (Dorian, 2026-09-14): celebrate on BOTH 28 Feb and 1 Mar in a
   common year, and on 29 Feb itself in a leap year. So a 29-Feb shopper gets
   two days three years in four, and one day in the fourth.

   ARMING ONCE PER DAY: the band's own ✕ writes birthday_overlay='false'. If
   this file re-armed on every page view, closing it would be pointless — it
   would come straight back. So it arms at most once per calendar day and
   records that in AGB_BirthdayArmed; after that the shopper's ✕ sticks.

   A ?birthday_overlay= LINK WINS, UNLESS IT IS SOMEBODY ELSE'S. The Drip
   birthday email arms the band with a link, and a link travels -- forwarded,
   pasted into a group chat, opened on a shared machine. So when the link says
   whose birthday it is (&bd=), it is checked against what this device already
   holds first:

     device holds a DIFFERENT birthday -> refused. The link is stripped and the
                                          normal date check below decides.
     device holds the SAME birthday    -> armed.
     device holds NOTHING              -> armed, and the date is learned.

   ONLY A READABLE DATE, ON ITS OWN DAY, CAN ARM FROM A LINK. Dorian's rule is a
   conjunction and both halves must hold:
     1. the device holds no birthday, or holds the same one the link carries
     2. today IS the date the link carries, give or take a day (`withinADay`,
        GRACE_DAYS = 1) -- the send timezone and the shopper's are not the same
        clock. THE LINK PATH ONLY: the date check further down stays exact, or
        every shopper would get a three-day band with no email involved.
        One consequence worth knowing: a shopper clicking the day before sees
        the band that day AND on the birthday itself, because the arming is
        stamped per day -- so "the ✕ sticks" means "sticks for that day".
   Either failing means no band. So a birthday email read a week late, or opened
   next March, does nothing -- though the date is still learned from it, so that
   browser self-arms next year with no email at all.

   A link with no &bd=, or one whose &bd= cannot be read, refuses as well: with no
   date there is nothing for condition 2 to be true of. That includes the bare
   ?birthday_overlay=true, which used to arm on any day -- type
   ?birthday_overlay=true&bd=<today's MM-DD> for the same result. There is no path
   that behaves one way for whoever is testing and another way for a shopper.

   A ?birthday_overlay=false link is never refused: turning the band OFF can do no
   harm.

   KILL SWITCH: window.AGB_BIRTHDAY_TRIGGER_OFF = true
   ISOLATION: own file, no DOM, no theme edits; it only reads/writes storage.
   ========================================================================== */
(function () {
  'use strict';

  if (window.AGB_BIRTHDAY_TRIGGER_OFF) return;
  if (window.__agbBirthdayTrigger) return;
  window.__agbBirthdayTrigger = true;

  var BAND_KEY = 'birthday_overlay';     // owned by agb-birthday-overlay.js
  var STORE_KEY = 'AGB_Birthday';        // written by /pages/birthday
  var ARMED_KEY = 'AGB_BirthdayArmed';   // 'YYYY-MM-DD' of the last arming
  // How many days either side of the birthday an emailed LINK may still arm on
  // (see withinADay below). UP HERE ON PURPOSE: linkDecides() runs before any
  // of the statements further down, so a `var` declared beside the helper would
  // still be undefined when it is read -- the loop bound would be NaN and every
  // link would refuse. Same trap as `today`/`year`, which is why celebrates()
  // takes them as arguments. 0 restores exact matching on the link.
  var GRACE_DAYS = 1;

  // ------------------------------------------------- the link, vetted first --
  // Deferred to the bottom of this file, because it needs validMonthDay() and
  // the storage helpers. Everything below runs only if the link did not settle
  // the matter.

  function get(k) {
    try { return localStorage.getItem(k); } catch (e) { return null; }
  }
  function set(k, v) {
    try { localStorage.setItem(k, v); } catch (e) { /* storage blocked */ }
  }

  function two(n) { return (n < 10 ? '0' : '') + n; }

  function isLeap(y) {
    return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  }

  // "MM-DD", or null. Accepts the ISO "YYYY-MM-DD" Drip stores too — that is
  // what `&bd={{ subscriber.Birthday }}` and the customer metafield hand back,
  // because Drip only recognises a date field in ISO form. The year is dropped:
  // a birthday recurs, so only month and day matter here.
  // Range-checked either way, because a stored value is just a string somebody
  // could have edited.
  function validMonthDay(raw) {
    var s = String(raw || '').trim();
    var iso = /^\d{4}-(\d{2}-\d{2})$/.exec(s);
    if (iso) s = iso[1];
    var m = /^(\d{2})-(\d{2})$/.exec(s);
    if (!m) return null;
    var mo = +m[1], d = +m[2];
    var len = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (mo < 1 || mo > 12 || d < 1 || d > len[mo - 1]) return null;
    return m[1] + '-' + m[2];
  }

  // ------------------------------------------------------- the arming link --
  // A ?birthday_overlay= link arms the band without asking what day it is, and
  // the overlay (which runs after this file) is what reads it. Before it gets
  // there: is this link for the person holding this device?
  //
  // The comparison is against what the device ALREADY knows -- the logged-in
  // customer's metafield first, then whatever this browser stored -- never
  // against the link's own &bd=, which is the thing being checked.
  function deviceBirthday() {
    try {
      var c = window.AGB_Customer;
      var fromCustomer = c && validMonthDay(c.birthday);
      if (fromCustomer) return fromCustomer;
    } catch (e) { /* not logged in, or no metafield */ }
    var raw = get(STORE_KEY);
    if (!raw) return null;
    try { return validMonthDay(JSON.parse(raw).date); } catch (e) { return null; }
  }

  // WHICH `bd` -- because there can be more than one. Drip's editor emits a
  // merge tag in an href TWICE: a percent-encoded copy that never renders, then
  // the real one. `qs.get()` hands back the FIRST, which on a real send is the
  // literal "{{ subscriber.Birthday }}" text. theme.liquid documents this for
  // `email=` and page.birthday.liquid scans every copy for the one with an "@";
  // this is the same discipline for the date. Returns:
  //   a date  -- the last copy validMonthDay accepts
  //   null    -- the parameter is not there at all
  //   false   -- it IS there and no copy could be read
  // Those last two are different answers and the caller treats them differently.
  function linkBirthday(qs) {
    var all;
    try { all = qs.getAll('bd'); } catch (e) { all = qs.has('bd') ? [qs.get('bd')] : []; }
    if (!all.length) return null;
    var found = null;
    for (var i = 0; i < all.length; i++) {
      var ok = validMonthDay(all[i]);
      if (ok) found = ok;                 // LAST readable copy wins
    }
    return found || false;
  }

  // STRIP THE URL ONLY AFTER THE GEO-REDIRECT HAS HAD ITS SAY (2026-09-22).
  // EasyLocation (async, in <head>) sends an Israeli visitor from agilitegear.com
  // to agilite.co.il -- and it builds that target from window.location.href AT
  // THE MOMENT ITS GEO LOOKUP ANSWERS. Measured on a warm load: the lookup
  // answered at 644 ms and this file ran at 645 ms. Whichever won the coin flip
  // decided whether the redirected URL still carried the arming parameters; if
  // the strip won, the IL page received the address (bde) and no band. The app
  // deletes nothing we need (only a parameter literally named `email`, which
  // theme.liquid has already copied into `bde`), so all that was ever lost was
  // what WE removed first. Decisions stay immediate; only the address-bar
  // clean-up waits -- until the app has written its decided-location cookie
  // (it prefixes every cookie with `el-`), or 6 s (its lookup times out at 5).
  // "Settled" is the app's OWN word for it: it dispatches
  // `easylocation_data_loaded` on document when its lookup has answered (the
  // cookie it reads, geoip-location, is written by its server and is absent
  // on a same-store visit, so it is only a second signal here). If instead it
  // announces `easylocation_redirect_after_check`, the page is about to leave
  // -- it navigates 5 ms later -- and the clean-up is skipped altogether,
  // because the one thing that must not happen now is a strip racing that
  // navigation. Either way the flag is already written and the band is up.
  function afterRedirectSettled(fn) {
    var started = Date.now(), settled = false, leaving = false;
    try {
      document.addEventListener('easylocation_data_loaded', function () { settled = true; });
      document.addEventListener('easylocation_redirect_after_check', function () { leaving = true; });
      settled = settled || /(^|;\s*)el-geoip-location=/.test(document.cookie || '');
    } catch (e) { /* no document (a harness): the timer alone decides */ }
    (function tick() {
      if (leaving) return;
      if (settled || Date.now() - started > 6000) { try { fn(); } catch (e) {} return; }
      setTimeout(tick, 250);
    })();
  }

  function stripParams(names) {
    try {
      var qs = new URLSearchParams(window.location.search);
      for (var i = 0; i < names.length; i++) qs.delete(names[i]);
      window.history.replaceState(null, '', window.location.pathname +
        (qs.toString() ? '?' + qs.toString() : '') + window.location.hash);
    } catch (e) { /* no history API -- the link just stays in the bar */ }
  }

  // Returns true when the link settled it and this file should stop.
  function linkDecides() {
    var qs;
    try { qs = new URLSearchParams(window.location.search); } catch (e) { return false; }
    if (!qs.has(BAND_KEY)) return false;
    // Turning the band OFF is never refused. It teaches nothing either -- a
    // disarm link says when to stop, not whose birthday it is -- so the date
    // is stripped rather than stored, and no path leaves one in the address bar.
    if (qs.get(BAND_KEY) !== 'true') {
      if (qs.has('bd')) stripParams(['bd']);
      // RECORD THE DISARM, exactly as the arming path records an arm. Returning
      // straight out let the band come back on the NEXT page view, and only on a
      // day celebrates() accepts: the overlay writes 'false' from the parameter
      // so the band did go down here, but nothing wrote ARMED_KEY, so the
      // arm-once branch below concluded it had not armed today and put it back.
      // The X never showed this because arming stamps the day first, which is
      // precisely what makes the X stick -- so the button worked and the
      // documented '=false' link did not. Found by an adversarial pass on
      // 2026-09-22; it predates the day rule rather than arriving with it.
      //
      // Writing the flag here as well as in the overlay costs nothing and makes
      // this file's own state honest. A valid arming link later the same day
      // still wins: it sets both keys on its way through.
      set(BAND_KEY, 'false');
      set(ARMED_KEY, stampFor(new Date()));
      return true;
    }

    var fromLink = linkBirthday(qs);
    var onDevice = deviceBirthday();
    // SENT BUT UNREADABLE IS NOT THE SAME AS NOT SENT. Absent (null) means
    // nobody claimed whose link this is -- a hand-typed ?birthday_overlay=true
    // -- and still wins. `false` means a date WAS sent and no copy of it parsed,
    // which is an empty merge tag or a mangled link; arming on that is how a
    // band ends up on a browser that has no business showing one.
    if (!fromLink) {
      // NOTHING READABLE, SO NOTHING TO SATISFY CONDITION 2. `false` is a date
      // that was sent and could not be read (an empty merge tag, a mangled
      // link); `null` is no date at all -- which used to arm, and was the
      // hand-typed demo path. Both refuse now, because the rule is a
      // conjunction and without a date the second half can never hold.
      // ?birthday_overlay=true&bd=<today's MM-DD> does what the bare parameter
      // used to do, so nothing is lost for testing and there is no path that
      // behaves one way for us and another way for a shopper.
      stripParams([BAND_KEY, 'bd']);
      window.AGB_BirthdayLinkRefused = { link: null, device: onDevice,
                                         reason: fromLink === false ? 'unreadable' : 'no date' };
      return false;
    }
    if (fromLink && onDevice && fromLink !== onDevice) {
      // SOMEBODY ELSE'S LINK. Strip both parameters so the overlay cannot arm
      // from it and so `bd` does not overwrite this device's own birthday, then
      // fall through: today may still be the birthday this device knows about.
      stripParams([BAND_KEY, 'bd']);
      window.AGB_BirthdayLinkRefused = { link: fromLink, device: onDevice };
      return false;
    }
    // ON THE DAY, OR NOT AT ALL. The link names a date and the band belongs to
    // that date: a birthday email read a week late, or forwarded and opened in
    // another month, must not raise it. Without this the link armed on ANY day,
    // for good.
    //
    // `celebrates` and `two` are function declarations and hoist, so they are
    // callable from here; `today` and `year` are vars assigned at the bottom of
    // the file and are still undefined, hence the local date.
    //
    // A DAY'S GRACE, because this is the SHOPPER'S local day and Drip sends on
    // the account's: a recipient far enough east or west of a midnight-adjacent
    // send is still on the previous local day when they click. `withinADay`
    // allows that one day either side -- on the LINK only; the date path below
    // stays exact.
    //
    // Without the grace it would cost them the click and not the day: the
    // refusal strips BAND_KEY and leaves `bd`, so syncFromLink() learns the date
    // and the date path arms after their own local midnight with no second
    // email. The shopper it actually saves is the one who clicks once, hours
    // early, and never comes back that day.
    {
      var clicked = new Date();
      var clickedMd = two(clicked.getMonth() + 1) + '-' + two(clicked.getDate());
      if (!withinADay(fromLink, clicked)) {
        // BAND_KEY only. `bd` is left for syncFromLink() below to consume, so a
        // late reader's browser still LEARNS the birthday -- and then arms on
        // its own, on the day, with no email involved: after local midnight for
        // a click that was merely early, and next year for one that was months
        // late. Stripping it here would throw all of that away. Returning false
        // rather than true because this device's own stored birthday may
        // legitimately be today, and the date path must still get its turn.
        stripParams([BAND_KEY]);
        window.AGB_BirthdayLinkStale = { link: fromLink, today: clickedMd };
        return false;
      }
    }
    // ALLOWED -- so this device should also LEARN the date, and the URL should
    // stop carrying it. Returning straight out skipped both: the band went up,
    // the browser was none the wiser, and somebody's birthday stayed in the
    // address bar to be shared along with the link.
    syncFromLink();
    // RECORD THE ARMING, which returning straight out did not. Without it the
    // band's X did not stick -- the next page view read ARMED_KEY as null,
    // concluded it had not armed today, and put the band back -- and the off-day
    // sweep below could never fire, so a band armed by a link stayed up on every
    // page forever. The overlay writes BAND_KEY from the parameter too; writing
    // it here as well costs nothing and makes this file's own state honest.
    set(BAND_KEY, 'true');
    set(ARMED_KEY, stampFor(new Date()));
    return true;                          // matches, or nothing to check it against
  }

  // 'YYYY-MM-DD' in the browser's own local time. One definition, used by the
  // link path and by the date check below, so they cannot disagree about what
  // day it is.
  function stampFor(d) {
    return d.getFullYear() + '-' + two(d.getMonth() + 1) + '-' + two(d.getDate());
  }

  // ------------------------------------------------------------ fix link --
  // CORRECTING A BIRTHDAY THIS BROWSER ALREADY HOLDS (2026-09-24, Dorian: "make
  // a system so i can really change it and it will update their local
  // storage"). Produced only by scripts/set-birthday.py after it corrects the
  // date in Drip:
  //     ?bdfix=<new MM-DD>&bdwas=<old MM-DD>&bde=<address>
  //
  // THE RULE: the stored date changes only if this browser holds EXACTLY the
  // old date Agilite had for that address. Nothing tries to decide whose device
  // this is -- two adversarial reviews showed that cannot be known here: the
  // page's `who` records whose link was opened on a device, and a forwarded
  // email sets it. So the proof is the date itself:
  //   - a forwarded fix link lands on a device holding somebody else's date,
  //     which does not equal `bdwas` -> refused;
  //   - a fix link reopened later finds the date already changed -> nothing
  //     to do;
  //   - an ordinary email link carries no `bdfix` and never reaches this block.
  // The one hole (review round 3): if a forwarded birthday-calendar email put
  // the RECIPIENT's date into the owner's Drip record, `bdwas` IS the
  // recipient's date, and the fix link forwarded to them again moves it. That
  // device looks exactly like the owner's own; nothing here can tell them apart.
  // Belt and braces: if the device recorded an owner (the page's `who`) and the
  // link names a different address, refused as well. It can only refuse more.
  //
  // It writes the date and nothing else: no band, no armed stamp. The date
  // path below decides the band from the corrected date as it always does, and
  // the address bar is cleaned only after the geo-redirect has had its say.
  function applyFixLink() {
    var qs;
    try { qs = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (!qs.has('bdfix')) return;
    afterRedirectSettled(function () {
      var now = new URLSearchParams(window.location.search);
      if (!now.has('bdfix') && !now.has('bdwas')) return;
      now.delete('bdfix'); now.delete('bdwas');
      window.history.replaceState(null, '', window.location.pathname +
        (now.toString() ? '?' + now.toString() : '') + window.location.hash);
    });
    function lastReadable(name) {
      var all = [], found = null;
      try { all = qs.getAll(name); } catch (e) { return null; }
      for (var i = 0; i < all.length; i++) { var ok = validMonthDay(all[i]); if (ok) found = ok; }
      return found;
    }
    function fingerprintOf(addr) {        // the page's own `who` fingerprint
      var s = String(addr || '').trim().toLowerCase();
      if (!s) return '';
      var h = 5381;
      for (var i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
      return h.toString(36);
    }
    function refuse(reason) { window.AGB_BirthdayFixRefused = { reason: reason }; }

    var to = lastReadable('bdfix'), was = lastReadable('bdwas');
    if (!to || !was) return refuse('unreadable');
    var addr = '';
    try {
      var all = qs.getAll('bde').concat(qs.getAll('email'));
      for (var i = 0; i < all.length; i++) { if (String(all[i]).indexOf('@') > -1) { addr = all[i]; break; } }
    } catch (e) {}
    if (!addr) return refuse('no address');

    var rec = null;
    try { rec = JSON.parse(get(STORE_KEY) || 'null'); } catch (e) { rec = null; }
    var held = rec && validMonthDay(rec.date);
    if (!held) return refuse('nothing stored');
    if (held === to) return;                       // already correct
    if (held !== was) return refuse('holds a different date');
    var owner = rec && typeof rec.who === 'string' ? rec.who : '';
    if (owner && owner !== fingerprintOf(addr)) return refuse('different owner');

    var copy = {};
    for (var k in rec) { if (Object.prototype.hasOwnProperty.call(rec, k)) copy[k] = rec[k]; }
    copy.date = to;
    copy.correctedAt = new Date().toISOString();
    set(STORE_KEY, JSON.stringify(copy));
    window.AGB_BirthdayFixed = { from: was, to: to };
  }

  applyFixLink();

  if (linkDecides()) {
    window.AGB_BirthdayTrigger = { link: true, celebrating: true };
    return;
  }

  // ---------------------------------------------------------- device sync --
  // Drip knows the birthday; a browser might not. Every Drip email can carry it
  // on its links as &bd={{ subscriber.Birthday }}, so the FIRST click from any
  // email — not just the birthday one — teaches that device the date.
  //
  // Deliberately NOT named `birthday`: on /pages/birthday that parameter means
  // "the shopper just picked this", which is a different act with a different
  // consequence. Conflating them would let a stale link re-save a birthday.
  //
  // A LINK MAY TEACH A BROWSER THAT KNOWS NOTHING; IT MAY NOT CONTRADICT ONE.
  // This used to read "Drip is the source of truth, so a value arriving here
  // overwrites the local copy" -- and a link is not Drip, it is whatever URL
  // somebody opened. `bd=` WITHOUT `birthday_overlay=` skips linkDecides()
  // entirely (it returns early when the band parameter is absent), so this
  // function was the whole of the defence and it only compared for equality.
  // One forwarded link and the recipient's browser held somebody else's
  // birthday: the band then armed on a stranger's date through the DATE path,
  // where none of the link refusals apply, and /pages/birthday showed it as
  // theirs. Vetted here because this is the only writer and the question is
  // about the write, not about the band.
  //
  // The parameter is then stripped from the URL either way, so a shared or
  // bookmarked link does not carry somebody's birthday around.
  function syncFromLink() {
    var bd;
    try {
      var qs = new URLSearchParams(window.location.search);
      // Same doubled-merge-tag reading as the guard, and the parameter is taken
      // out of the URL EITHER WAY -- an unreadable date is still somebody's
      // birthday sitting in the address bar to be shared along with the link.
      var read = linkBirthday(qs);
      bd = read || null;
      if (!qs.has('bd')) return null;
      // Deferred, not immediate -- see afterRedirectSettled(). A refused link has
      // already had its parameters removed by stripParams(), immediately, so
      // this only ever delays the clean-up of a link that was ALLOWED (or a
      // stale one whose band parameter is already gone). Re-read the URL at
      // strip time: the overlay may have removed its own parameter meanwhile.
      afterRedirectSettled(function () {
        var now = new URLSearchParams(window.location.search);
        if (!now.has('bd')) return;
        now.delete('bd');
        window.history.replaceState(null, '', window.location.pathname +
          (now.toString() ? '?' + now.toString() : '') + window.location.hash);
      });
      if (!bd) return null;
    } catch (e) { return bd || null; }

    var prior = null;
    try { prior = JSON.parse(get(STORE_KEY) || 'null'); } catch (e) {}
    if (prior && prior.date === bd) return bd;          // already knew it

    // CONTRADICTION IS REFUSED WHEREVER IT ARRIVES. Same question linkDecides
    // asks, asked again at the write, because a `bd` with no band parameter
    // never went past that function. Returning null rather than the link's date
    // lets storedBirthday() fall through to what this device already holds --
    // which is the answer that should win.
    var held = deviceBirthday();
    if (held && held !== bd) {
      window.AGB_BirthdayLinkRefused = { link: bd, device: held, reason: 'would overwrite' };
      return null;
    }

    var now = new Date().toISOString();
    set(STORE_KEY, JSON.stringify({
      date: bd,
      savedAt: now,
      firstSavedAt: (prior && (prior.firstSavedAt || prior.savedAt)) || now,
      source: 'link'
    }));
    return bd;
  }

  function storedBirthday() {
    // 1. Straight off a Drip link — the freshest thing we can be handed, and
    //    the only source that teaches a brand-new device.
    var fromLink = syncFromLink();
    if (fromLink) return fromLink;
    // 2. The Shopify customer metafield, if one is ever defined. Same on every
    //    device, but only ever populated for a logged-in shopper.
    try {
      var c = window.AGB_Customer;
      var fromCustomer = c && validMonthDay(c.birthday);
      if (fromCustomer) return fromCustomer;
    } catch (e) { /* fall through to local */ }
    // 3. What this browser already knew.
    var raw = get(STORE_KEY);
    if (!raw) return null;
    try { return validMonthDay(JSON.parse(raw).date); } catch (e) { return null; }
  }

  // Does `today` count as the shopper's birthday?
  function celebrates(birthday, today, year) {
    if (birthday === today) return true;
    // 29 Feb in a common year: both neighbours count.
    if (birthday === '02-29' && !isLeap(year)) {
      return today === '02-28' || today === '03-01';
    }
    return false;
  }

  // A DAY EITHER SIDE OF THE BIRTHDAY -- the LINK path's question, never the
  // date path's. Drip sends on the account's timezone and this file reads the
  // shopper's, so a click can legitimately land on the day before or after.
  //
  // The date path must NOT use this: it asks "is it this shopper's birthday
  // today", and a tolerance there would give every shopper a three-day band,
  // forever, with no email involved.
  //
  // Shifts the CLICKED day rather than the birthday, and asks celebrates() each
  // time. Two things fall out of that:
  //   - `new Date(y, m, d + offset)` normalises, so 31 December +1 really is
  //     1 January of the next year and the day before 1 March is the 28th or
  //     the 29th according to the year. "MM-DD" string maths gets both wrong.
  //   - the 29-February rule is composed with rather than reimplemented:
  //     celebrates() already maps 02-29 to {02-28, 03-01} in a common year, so
  //     with the grace a leap-day shopper gets {02-27, 02-28, 03-01, 03-02}.
  //     Deliberate, and pinned by a case.
  //
  // GRACE_DAYS (declared with the other constants at the top, because this
  // function is CALLED from linkDecides long before this line would run) = 0
  // turns it off and restores exact matching on the link.
  function withinADay(birthday, clicked) {
    for (var offset = -GRACE_DAYS; offset <= GRACE_DAYS; offset++) {
      var d = new Date(clicked.getFullYear(), clicked.getMonth(), clicked.getDate() + offset);
      if (celebrates(birthday, two(d.getMonth() + 1) + '-' + two(d.getDate()), d.getFullYear())) {
        return true;
      }
    }
    return false;
  }

  var now = new Date();
  var year = now.getFullYear();
  var today = two(now.getMonth() + 1) + '-' + two(now.getDate());
  var stamp = stampFor(now);               // 'YYYY-MM-DD', local time
  var birthday = storedBirthday();

  if (birthday && celebrates(birthday, today, year)) {
    // Arm once. If we already armed today, leave the flag alone so a ✕ sticks.
    if (get(ARMED_KEY) !== stamp) {
      set(BAND_KEY, 'true');
      set(ARMED_KEY, stamp);
    }
  } else {
    // Not their birthday: make sure a flag left over from the big day does not
    // greet them on some random visit in July.
    //
    // COMPARES THE DAY, not merely whether a day was recorded. Testing for
    // presence meant a band armed by a link -- which recorded nothing -- could
    // never be swept. It also lets a link-armed band survive its own local day
    // when this browser's date disagrees with the one Drip sent on: the account's
    // timezone and the shopper's are not the same clock, and the link is the
    // more deliberate signal of the two.
    // AND IT IGNORES "ALREADY ARMED TODAY" (Dorian, 2026-09-22: "the website
    // should disable itself the overlay when not on the right day", and "the
    // link should DEACTIVATE the overlay if wrong day").
    //
    // The old test was `get(ARMED_KEY) !== stamp`, which let ANY band stamped
    // today survive the whole day -- including one switched on by hand, or by a
    // link, on a day that is nobody's birthday. That is exactly what was seen
    // two days after a 20 September birthday: the band had been armed by hand
    // that morning, so the stamp said today, so this branch declined to touch
    // it, and clicking the real email link did not clear it either -- a stale
    // link only declines to ARM, it never turned anything off.
    //
    // Now the stamp only protects a day the birthday can actually justify:
    // the day itself is handled above, and `withinADay` covers the day either
    // side, which is the timezone grace a link is allowed to arm on. Anything
    // else is off, whatever the stamp says. A stale link lands here too --
    // linkDecides() returns false for one -- so the link deactivates as asked,
    // without a second place that writes the flag.
    //
    // The X still sticks: it writes 'false', and the arm-once branch above will
    // not put it back while the stamp says today.
    if (get(BAND_KEY) === 'true') {
      var graceDay = !!(birthday && get(ARMED_KEY) === stamp && withinADay(birthday, now));
      if (!graceDay) set(BAND_KEY, 'false');
    }
  }

  // Say what was decided, for anyone listening (and for the console).
  window.AGB_BirthdayTrigger = {
    birthday: birthday,
    today: today,
    celebrating: !!(birthday && celebrates(birthday, today, year))
  };
})();
